/**
 * The tasks under a ticket — legacy's Tasks tab.
 *
 * There, the tab mounted the whole help desk list again with `mode="children"`
 * and a parent id, which meant the child list carried the parent screen's
 * analytics, filters, archive toggle and sort control for a list that is
 * usually three rows. Here it is the three rows, each a link to itself, with
 * the one action a task list needs.
 */

import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ListTree, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { TicketForm } from "@/components/helpdesk/ticket-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonLines } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip";
import {
  isClosed,
  PRIORITY_LABEL,
  PRIORITY_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  tasksQuery,
  type Ticket,
} from "@/services/helpdesk";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";

export function TicketTasks({ ticket }: { ticket: Ticket }) {
  const t = useTranslation();
  const allow = useCan();
  const tasks = useQuery(tasksQuery(ticket.id));
  const [adding, setAdding] = useState(false);

  const closed = isClosed(ticket.status);
  const rows = tasks.data?.items ?? [];
  const mayAdd = allow("add__helpdesk_ticket");

  const addButton = mayAdd ? (
    closed ? (
      <Tooltip label={t("hd.closedTasks")}>
        <span>
          <Button disabled>
            <Plus className="size-4" aria-hidden />
            {t("hd.addTask")}
          </Button>
        </span>
      </Tooltip>
    ) : (
      <Button onClick={() => setAdding(true)}>
        <Plus className="size-4" aria-hidden />
        {t("hd.addTask")}
      </Button>
    )
  ) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-title-3 text-heading">{t("hd.tasks")}</h3>
        {addButton}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {tasks.isLoading ? (
          <SkeletonLines rows={3} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ListTree}
            title={t("hd.noTasksTitle")}
            description={t("hd.noTasksBody")}
            {...(mayAdd && !closed ? { action: addButton } : {})}
          />
        ) : (
          <ol className="flex flex-col gap-2">
            {rows.map((task) => (
              <li key={task.id}>
                <Link
                  to={`/helpdesk/${task.id}`}
                  className="rounded-card bg-surface-raised hover:bg-surface-sunken flex flex-wrap items-center gap-3 p-3.5 transition-colors"
                >
                  <span className="text-fg-subtle text-footnote font-mono">
                    {task.reference}
                  </span>
                  <span className="text-fg text-body min-w-0 flex-1 truncate">
                    {task.title}
                  </span>
                  <Badge tone={STATUS_TONE[task.status]}>{t(STATUS_LABEL[task.status])}</Badge>
                  <Badge tone={PRIORITY_TONE[task.priority]}>
                    {t(PRIORITY_LABEL[task.priority])}
                  </Badge>
                  <span className="text-fg-subtle text-footnote">
                    {task.due_date ? format(new Date(task.due_date), "MMM d, yyyy") : "—"}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        )}
      </div>

      {adding ? (
        <TicketForm ticket={null} parentId={ticket.id} onClose={() => setAdding(false)} />
      ) : null}
    </div>
  );
}
